import bwipjs from 'bwip-js';
import QRCode from 'qrcode';
import { BARCODE_VALIDATION, buildScannerPayload, type ScannerKeys } from '@/types/label';

// Map our friendly type names to bwip-js bcid values
const BCID_MAP: Record<string, string> = {
  code128: 'code128',
  code39: 'code39',
  code93: 'code93',
  ean13: 'ean13',
  ean8: 'ean8',
  upca: 'upca',
  upc: 'upca', // legacy alias
  itf14: 'itf14',
  interleaved2of5: 'interleaved2of5',
  'gs1-128': 'gs1-128',
  datamatrix: 'datamatrix',
  pdf417: 'pdf417',
  codabar: 'rationalizedCodabar',
  msi: 'msi',
  pharmacode: 'pharmacode',
  telepen: 'telepen',
};

// Whether a barcode type is 2D (no bar height / text below)
const IS_2D: Record<string, boolean> = {
  datamatrix: true,
  pdf417: true,
};

/**
 * Validate barcode content against type-specific rules.
 * Returns null if valid, or an error message string.
 */
/**
 * bwip-js cannot encode raw control characters. Convert them to BWIPP
 * caret escapes (^NNN) and enable `parse`. Literal carets are escaped too.
 */
const toBwippEscaped = (input: string): { text: string; needsParse: boolean } => {
  let needsParse = false;
  let out = '';
  for (const ch of input) {
    const code = ch.charCodeAt(0);
    if (code < 32 || code === 127) {
      needsParse = true;
      out += '^' + String(code).padStart(3, '0');
    } else if (ch === '^') {
      needsParse = true;
      out += '^094';
    } else {
      out += ch;
    }
  }
  return { text: out, needsParse };
};

export const validateBarcodeContent = (content: string, barcodeType: string): string | null => {
  if (!content || !content.trim()) return 'Content is empty';
  const rules = BARCODE_VALIDATION[barcodeType];
  if (!rules) return null;
  if (rules.minLength && content.length < rules.minLength) {
    return `Minimum ${rules.minLength} characters required`;
  }
  if (rules.maxLength && content.length > rules.maxLength) {
    return `Maximum ${rules.maxLength} characters allowed`;
  }
  if (rules.regex && !rules.regex.test(content)) {
    return `Invalid format: ${rules.message}`;
  }
  // Special check: interleaved 2 of 5 needs even length
  if (barcodeType === 'interleaved2of5' && content.length % 2 !== 0) {
    return 'Must have an even number of digits';
  }
  return null;
};

export const generateBarcodeDataUrl = async (
  text: string,
  width: number,
  height: number,
  includeText: boolean = true,
  barScale: number = 2,
  barcodeType: string = 'code128',
  barHeightRatio: number = 0.8,
  quietZone: number = 2,
  foregroundColor?: string,
  backgroundColor?: string,
  textSize?: number,
  scannerKeys?: ScannerKeys,
): Promise<string> => {
  try {
    const canvas = document.createElement('canvas');
    const bcid = BCID_MAP[barcodeType] || 'code128';
    const is2D = IS_2D[barcodeType] || false;

    // Allow wider range: 0.5-8 for bar scale
    const scale = Math.max(0.5, Math.min(8, barScale));
    const heightRatio = Math.max(0.1, Math.min(1.0, barHeightRatio));

    // Prepare text - gs1-128 needs FNC1 parsing
    let encodedText = text || ' ';
    if (barcodeType === 'gs1-128') {
      // bwip-js expects raw AI data; convert (01)xxx(10)yyy to ^FNC1 delimited
      encodedText = encodedText.replace(/\((\d{2,4})\)/g, '^$1');
      if (!encodedText.startsWith('^')) encodedText = '^' + encodedText;
    }

    // Scanner keystrokes (control characters) around / between the data
    const payload = buildScannerPayload(encodedText, scannerKeys);
    const hasKeystrokes = payload.hasKeys;
    const humanText = payload.display;
    encodedText = payload.encoded;

    const isGs1 = barcodeType === 'gs1-128';
    const escaped = isGs1
      ? { text: encodedText, needsParse: true }
      : toBwippEscaped(encodedText);

    const opts: any = {
      bcid,
      text: escaped.text,
      parse: escaped.needsParse || undefined,
      scale,
      paddingleft: quietZone,
      paddingright: quietZone,
      paddingtop: 1,
      paddingbottom: 1,
    };

    // Colors
    if (foregroundColor) {
      opts.barcolor = foregroundColor.replace('#', '');
    }
    if (backgroundColor) {
      opts.backgroundcolor = backgroundColor.replace('#', '');
    }

    if (is2D) {
      // 2D barcodes don't have bar height or text below
      opts.scale = Math.max(2, scale);
    } else {
      opts.height = includeText
        ? Math.max(5, height * heightRatio * 0.85)
        : Math.max(5, height * heightRatio);
      opts.includetext = includeText;
      opts.textxalign = 'center';
      opts.textsize = textSize || 8;
      if (hasKeystrokes) opts.alttext = humanText;
    }

    await (bwipjs as any).toCanvas(canvas, opts);
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Barcode generation error:', error, { barcodeType, text });
    return '';
  }
};

export const generateQRCodeDataUrl = async (
  text: string,
  size: number,
  foregroundColor?: string,
  backgroundColor?: string,
  scannerKeys?: ScannerKeys,
): Promise<string> => {
  try {
    const payload = buildScannerPayload(text || ' ', scannerKeys).encoded;
    const dataUrl = await QRCode.toDataURL(payload, {
      width: size,
      margin: 0,
      errorCorrectionLevel: 'M',
      color: {
        dark: foregroundColor || '#000000',
        light: backgroundColor || '#FFFFFF',
      },
    });
    return dataUrl;
  } catch (error) {
    console.error('QR code generation error:', error);
    return '';
  }
};

export const generateBarcodeCanvas = async (
  text: string,
  widthMm: number,
  heightMm: number,
  dpi: number,
  includeText: boolean = true,
  barScale: number = 2,
  barcodeType: string = 'code128',
  barHeightRatio: number = 0.8,
  quietZone: number = 2,
  foregroundColor?: string,
  backgroundColor?: string,
  textSize?: number,
  scannerKeys?: ScannerKeys,
): Promise<HTMLCanvasElement> => {
  const canvas = document.createElement('canvas');
  const widthPx = (widthMm / 25.4) * dpi;
  const heightPx = (heightMm / 25.4) * dpi;
  
  canvas.width = widthPx;
  canvas.height = heightPx;
  
  const bcid = BCID_MAP[barcodeType] || 'code128';
  const is2D = IS_2D[barcodeType] || false;
  const scale = Math.max(0.5, Math.min(8, barScale)) * (dpi / 75);
  const heightRatio = Math.max(0.1, Math.min(1.0, barHeightRatio));
  
  try {
    // Prepare text - gs1-128 needs FNC1 parsing
    let encodedText = text || ' ';
    if (barcodeType === 'gs1-128') {
      encodedText = encodedText.replace(/\((\d{2,4})\)/g, '^$1');
      if (!encodedText.startsWith('^')) encodedText = '^' + encodedText;
    }

    const payload = buildScannerPayload(encodedText, scannerKeys);
    const hasKeystrokes = payload.hasKeys;
    const humanText = payload.display;
    encodedText = payload.encoded;

    const isGs1 = barcodeType === 'gs1-128';
    const escaped = isGs1
      ? { text: encodedText, needsParse: true }
      : toBwippEscaped(encodedText);

    const opts: any = {
      bcid,
      text: escaped.text,
      parse: escaped.needsParse || undefined,
      scale,
      paddingleft: quietZone,
      paddingright: quietZone,
      paddingtop: 1,
      paddingbottom: 1,
    };

    if (foregroundColor) {
      opts.barcolor = foregroundColor.replace('#', '');
    }
    if (backgroundColor) {
      opts.backgroundcolor = backgroundColor.replace('#', '');
    }

    if (is2D) {
      opts.scale = Math.max(2, scale);
    } else {
      opts.height = Math.max(5, heightMm * heightRatio);
      opts.includetext = includeText;
      opts.textxalign = 'center';
      opts.textsize = textSize || Math.max(6, heightMm * 0.15);
      if (hasKeystrokes) opts.alttext = humanText;
    }

    await (bwipjs as any).toCanvas(canvas, opts);
  } catch (error) {
    console.error('Barcode canvas generation error:', error, { barcodeType, text });
  }
  
  return canvas;
};

export const generateQRCodeCanvas = async (
  text: string,
  sizeMm: number,
  dpi: number,
  foregroundColor?: string,
  backgroundColor?: string,
  scannerKeys?: ScannerKeys,
): Promise<HTMLCanvasElement> => {
  const sizePx = Math.round((sizeMm / 25.4) * dpi);
  
  const canvas = document.createElement('canvas');
  canvas.width = sizePx;
  canvas.height = sizePx;
  
  try {
    const payload = buildScannerPayload(text || ' ', scannerKeys).encoded;
    await QRCode.toCanvas(canvas, payload, {
      width: sizePx,
      margin: 0,
      errorCorrectionLevel: 'M',
      color: {
        dark: foregroundColor || '#000000',
        light: backgroundColor || '#FFFFFF',
      },
    });
  } catch (error) {
    console.error('QR canvas generation error:', error);
  }
  
  return canvas;
};
